import React, {useEffect, useState} from "react";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Fade,
    Menu,
    MenuItem,
    styled,
    Tooltip
} from "@mui/material";
import {DateRangePicker} from "react-date-range";
import {convertDateToString} from "../utils/cert-helper";
import {getQueryString} from "../utils/http-helpers";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import ShowSnackbar from "../utils/ShowSnackbar";
import {StyledQueryDiv, StyledSearchDiv} from "../Contributions/ContributionSearchPanel";
import {useDispatch, useSelector} from "react-redux";
import {closeAllHispsErrorSnackbar, getAllHisps} from "../features/allHispsSlice";
import {customRangeLabels} from "../utils/customStyles";

const StyledDialog = styled(Dialog)({
    '& .MuiDialog-paper': {
        maxWidth: '1000px',
    }
})

const typeArray = [
    'UserActivity',
    'ApiActivity',
    'NpiDbManagement',
    'TaxonomyDbManagement',
    'CredentialDbManagement',
    'DepartmentNameDbManagement',
    'DirectoryCSV',
    'Validation',
    'SQSActivity',
    'S3Activity',
    'Publish',
    'HispManagement',
    'HISPActivity',
    'BinaryManagement'
]


export default function AuditLogsSearchPanel (props) {
    const onQueryStringChanged = props.onQueryStringChanged;
    const onRefresh = props.onRefresh;
    const setPage = props.setPage;

    const [params, setParams] = useState({});
    const [queryString, setQueryString] = useState("");

    const [hispAnchorEl, setHispAnchorEl] = useState(null);
    const [hispName, setHispName] = useState("Select a Hisp")
    const [typeAnchorEl, setTypeAnchorEl] = useState(null);
    const [type, setType] = useState("Select a Type");

    // react redux
    const { hisps, allHispsError, allHispsErrorMessage } = useSelector(state => state.allHisps);
    const dispatch = useDispatch();


    const hispOpen = Boolean(hispAnchorEl);
    const typeOpen = Boolean(typeAnchorEl);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [dateRange, setDateRange] = useState({
        startDate: "",
        endDate: "",
    });
    const [state, setState] = useState([
        {
            startDate: null,
            endDate: new Date(""),
            key: 'selection'
        }
    ]);

    useEffect(() => {
        console.log("useEffect, query String updated: ", queryString);
        onQueryStringChanged(queryString);
    }, [queryString])

    useEffect(() => {
        dispatch(getAllHisps());
    }, [])

    const handleHispClose = (event) => {
        const value = event.target.id;
        const newParams = {...params};
        if (value === "" || value === 'close') {
            setHispName("Select a Hisp");
            if (params.hisp !== undefined) {
                delete params.hisp;
            }
        } else {
            setHispName(value);
            newParams.hisp = value;
        }
        setParams(newParams);
        setHispAnchorEl(null);
    };

    const handleTypeClose = (event) => {
        const value = event.target.id;
        const newParams = {...params};
        if (value === "" || value === 'close') {
            setType("Select a Type");
            if (params.type !== undefined) {
                delete params.type;
            }
        } else {
            setType(value);
            newParams.type = value;
        }

        setParams(newParams);
        setTypeAnchorEl(null);
    }

    const handleDateChange = (item) => {
        const newParams = {...params};
        setState([item.selection]);
        setDateRange({
            startDate: convertDateToString(item.selection.startDate),
            endDate: convertDateToString(item.selection.endDate),
        })

        if (item === "") {
            if (params.startDate !== undefined || params.endDate !== undefined) {
                delete params.startDate;
                delete params.endDate;
            }
        } else {
            newParams.startDate = convertDateToString(item.selection.startDate);
            newParams.endDate = convertDateToString(item.selection.endDate);
        }
        setParams(newParams);
    }

    const handleSearch = () => {
        const s = getQueryString(params);
        console.log("search ", params, s);
        setPage(0);
        setQueryString(s);
    }

    const handleResetClick = () => {
        setPage(0);
        setParams({});
        setQueryString(getQueryString({}));
        setHispName("Select a Hisp");
        setType("Select a Type");
        setDateRange({startDate: "", endDate: ""});
        setState([
            {
                startDate: null,
                endDate: new Date(""),
                key: 'selection'
            }
        ])
    }

    const handleRefresh = () => {
        onRefresh(new Date().getTime());
    }

    const handleDateRangePickerReset = () => {
        const newParam = {...params}
        setDateRange({
            startDate: "",
            endDate: "",
        })
        setState([
            {
                startDate: null,
                endDate: new Date(""),
                key: 'selection'
            }
        ])
        delete newParam.startDate;
        delete newParam.endDate;

        setParams(newParam);
    }

    return (
        <div>
            <StyledQueryDiv>
                <StyledDialog
                    open={dialogOpen}
                    onClose={handleHispClose}
                >
                    <DialogTitle>{"Please select a date range"}</DialogTitle>
                    <DialogContent>
                        <DateRangePicker
                            onChange={item => handleDateChange(item)}
                            showSelectionPreview={true}
                            moveRangeOnFirstSelection={false}
                            months={2}
                            ranges={state}
                            staticRanges={customRangeLabels}
                            direction="horizontal"
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleDateRangePickerReset} variant='outlined'>RESET</Button>
                        <Button onClick={() => setDialogOpen(false)} variant='contained'>Confirm</Button>
                    </DialogActions>
                </StyledDialog>
                <Button
                    id="hisps-button"
                    aria-controls={hispOpen ? 'hisps-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={hispOpen ? 'true' : undefined}
                    onClick={(event) => setHispAnchorEl(event.currentTarget)}
                    variant="text"
                    endIcon={<KeyboardArrowDownIcon/>}
                >
                    {hispName}
                </Button>
                <Menu
                    id="hisps-menu"
                    anchorEl={hispAnchorEl}
                    open={hispOpen}
                    onClose={handleHispClose}
                    MenuListProps={{
                        'aria-labelledby': 'hisps-button',
                    }}
                    TransitionComponent={Fade}
                >
                    <MenuItem id='close' onClick={handleHispClose}>Select A Hisp</MenuItem>
                    {hisps && hisps.map(hisp => (
                        <MenuItem id={hisp.hispId} key={hisp.hispId} onClick={handleHispClose}>{hisp.hispName}</MenuItem>
                    ))}
                </Menu>

                <Button
                    id="type-button"
                    aria-controls={typeOpen ? 'type-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={typeOpen ? 'true' : undefined}
                    onClick={(event) => setTypeAnchorEl(event.currentTarget)}
                    variant="text"
                    endIcon={<KeyboardArrowDownIcon/>}
                >
                    {type}
                </Button>
                <Menu
                    id="type-menu"
                    anchorEl={typeAnchorEl}
                    open={typeOpen}
                    onClose={handleTypeClose}
                    MenuListProps={{
                        'aria-labelledby': 'type-button',
                    }}
                >
                    <MenuItem id='close' onClick={handleTypeClose}>Select A Type</MenuItem>
                    {typeArray && typeArray.map((type, i) => (
                        <MenuItem id={type} key={i} onClick={handleTypeClose}>{type}</MenuItem>
                    ))}
                </Menu>
                <Tooltip title="default date is current date" arrow sx={{fontSize: '14px'}}>
                    <Button
                        onClick={() => setDialogOpen(true)}
                        variant="text"
                        endIcon={<KeyboardArrowDownIcon/>}
                    >
                        {dateRange.startDate !== "" ? `${dateRange.startDate} - ${dateRange.endDate}` : "Select a date range"}
                    </Button>
                </Tooltip>
            </StyledQueryDiv>
            <StyledSearchDiv>
                <Button variant='contained' onClick={handleSearch} startIcon={<SearchIcon/>}>
                    Search
                </Button>
                <Button variant='outlined' onClick={handleRefresh} startIcon={<RefreshIcon />}>
                    Refresh
                </Button>
                <Button variant='outlined' onClick={handleResetClick}>
                    Reset
                </Button>
            </StyledSearchDiv>
            {allHispsError && <ShowSnackbar open={allHispsError} close={() => dispatch(closeAllHispsErrorSnackbar())}
                                            msg={{msgLv: 'error', msg: `${allHispsErrorMessage}`}}/>}
        </div>
    )
}
